import { useEffect, useId, useState } from 'react';
import { cn } from '@/lib/cn';
import { useTheme } from '@/store/theme-context';
import type { ResolvedTheme } from '@/utils/theme';
import { CodeContent } from './code-content';
import { CopyButton } from './CopyButton';

// `mermaid` is heavy (pulls in d3/dagre). Load it lazily via a module-level
// promise so it is code-split out of the main bundle and only fetched the
// first time a diagram actually renders.
type Mermaid = typeof import('mermaid')['default'];
let loadPromise: Promise<Mermaid> | null = null;

function loadMermaid(): Promise<Mermaid> {
  if (!loadPromise) {
    loadPromise = import('mermaid').then((m) => m.default);
  }
  return loadPromise;
}

// The surface diagrams render on. Edge labels get this as an opaque fill so
// they mask the arrows they overlap while still matching the background.
// Resolved to a concrete color (not a `hsl(var(--card))` string) because
// mermaid's theme pipeline can't parse CSS-var expressions.
function resolveSurfaceColor(): string {
  if (typeof document === 'undefined') return '#ffffff';
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue('--card')
    .trim();
  return raw ? `hsl(${raw})` : '#ffffff';
}

// mermaid.initialize is global; re-run it when the theme or the resolved
// surface color changes (the latter also covers theme-variant switches that
// don't flip the light/dark mode).
let initializedKey: string | null = null;

function ensureThemeInitialized(mermaid: Mermaid, theme: ResolvedTheme) {
  const wanted = theme === 'dark' ? 'dark' : 'default';
  const surface = resolveSurfaceColor();
  const key = `${wanted}|${surface}`;
  if (initializedKey === key) return;
  mermaid.initialize({
    startOnLoad: false,
    // Content is agent/LLM-generated — mermaid's own DOMPurify pass is the
    // only sanitizer on this path (the app's rehype-sanitize doesn't see the
    // SVG, which is produced inside this React component, not injected as raw
    // HTML). 'strict' strips HTML tags in labels while rendering inline.
    securityLevel: 'strict',
    theme: wanted,
    fontFamily: 'ui-sans-serif, system-ui, -apple-system, sans-serif',
    // Match the edge-label box to the diagram's surface so it hides the
    // arrows underneath without showing a contrasting grey block.
    themeVariables: { edgeLabelBackground: surface },
  });
  initializedKey = key;
}

// Monotonic suffix guarantees a unique render id per render() call, so a
// StrictMode double-invoke (two effects with the same component id running
// concurrently) can't collide on the temporary DOM node mermaid creates.
let renderSeq = 0;

interface MermaidDiagramProps {
  code: string;
  className?: string;
}

/**
 * Renders a Mermaid fenced-code block as an inline SVG diagram. Shared by the
 * read-only `MarkdownContent` renderer and the MDX editor's code-block
 * descriptor. Invalid diagrams (common from LLM output) degrade gracefully to
 * the original source rendered as a `mermaid`-labelled code block.
 */
export function MermaidDiagram({ code, className }: MermaidDiagramProps) {
  const { resolvedTheme } = useTheme();
  // useId() returns colon-wrapped strings (`:r1:`) that are invalid as DOM/CSS
  // ids — strip to an alphanumeric base mermaid can use.
  const baseId = `mermaid-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSvg(null);
    setError(false);

    (async () => {
      try {
        const mermaid = await loadMermaid();
        if (cancelled) return;
        ensureThemeInitialized(mermaid, resolvedTheme);
        // Throws on invalid syntax → caught below and rendered as a fallback.
        await mermaid.parse(code);
        const renderId = `${baseId}-${(renderSeq += 1)}`;
        const { svg: rendered } = await mermaid.render(renderId, code);
        if (!cancelled) setSvg(rendered);
      } catch {
        if (!cancelled) setError(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [code, resolvedTheme, baseId]);

  // Malformed diagram — fall back to the source as a labelled code block so
  // nothing is lost and the page never crashes.
  if (error) {
    return (
      <CodeContent language="mermaid" label="mermaid" className={className}>
        {code}
      </CodeContent>
    );
  }

  if (!svg) {
    return (
      <div
        className={cn(
          'flex items-center justify-center p-6 text-xs text-muted-foreground',
          className,
        )}
      >
        Rendering diagram…
      </div>
    );
  }

  return (
    <div
      className={cn('group relative overflow-x-auto', className)}
    >
      <div className="absolute right-1 top-1 z-10 opacity-0 transition-opacity group-hover:opacity-100">
        <CopyButton
          value={code}
          title="Copy diagram source"
          successLabel="Copied source"
          className="bg-card/80 backdrop-blur"
        />
      </div>
      {/* SVG is mermaid-sanitized (securityLevel: 'strict'). Constrain it to
          the container width and center it. The edge-label rules give the
          arrow labels breathing room inside their surface-coloured box: the
          foreignObject wrapping each label is sized to the bare text and clips
          by default, so allow overflow and pad the inner span. */}
      <div
        className={cn(
          'flex justify-center p-4 [&_svg]:h-auto [&_svg]:max-w-full',
          '[&_foreignObject]:!overflow-visible',
          // mermaid paints two stacked label backgrounds — the semi-transparent
          // square `.labelBkg` wrapper and the inner span. Drop the wrapper so
          // only our single rounded, padded span box shows.
          '[&_.labelBkg]:!bg-transparent',
          '[&_span.edgeLabel]:!inline-block [&_span.edgeLabel]:!rounded [&_span.edgeLabel]:!px-1 [&_span.edgeLabel]:!py-0',
        )}
        dangerouslySetInnerHTML={{ __html: svg }}
      />
    </div>
  );
}

export default MermaidDiagram;
